import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      quote: "Dr. Coradin has transformed my approach to health. Having direct access to him 24/7 gives me peace of mind that's invaluable in my demanding role as CEO. The comprehensive testing revealed issues I never knew existed.",
      name: "Michael Richardson",
      title: "CEO, Richardson Capital Group",
      image: "https://public.readdy.ai/ai/img_res/56ae2f1a713fa049f5f910ba880443a9.jpg"
    },
    {
      quote: "CorexMed isn't just healthcare—it's a partnership. Dr. Coradin's expertise in longevity medicine has helped me optimize my performance both professionally and personally. I finally have a doctor who truly understands my lifestyle.",
      name: "Sarah Chen",
      title: "Managing Partner, Chen & Associates",
      image: "https://readdy.ai/api/search-image?query=professional%20confident%20female%20executive%20businesswoman%20in%20elegant%20business%20attire%20modern%20office%20setting%20natural%20lighting%20clean%20simple%20background%20corporate%20portrait&width=80&height=80&seq=testimonial2&orientation=squarish"
    },
    {
      quote: "The level of personalized care is extraordinary. From hormone optimization to cardiovascular health, every aspect of my wellness is managed with precision. This is healthcare designed for people who refuse to compromise.",
      name: "James Wellington",
      title: "Founder, Wellington Ventures",
      image: "https://readdy.ai/api/search-image?query=distinguished%20professional%20executive%20businessman%20in%20premium%20suit%20confident%20pose%20modern%20corporate%20environment%20natural%20lighting%20clean%20simple%20background&width=80&height=80&seq=testimonial3&orientation=squarish"
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page-level SEO */}
      <title>CorexMed – Miramar Florida Executive Medicine | Concierge Doctor Dr. Coradin</title>
      <meta name="description" content="CorexMed offers Miramar Florida executive medicine with 24/7 concierge doctor access to Dr. David Coradin. Double board-certified in longevity medicine, regenerative medicine, hormone optimization & AI cardiac testing. Telemedicine nationwide." />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        "name": "CorexMed",
        "description": "Executive medicine practice offering comprehensive concierge healthcare with 24/7 access, specializing in longevity medicine, regenerative medicine, hormone optimization, AI cardiac testing, and personalized care.",
        "url": import.meta.env.VITE_SITE_URL ?? 'https://example.com',
        "telephone": "+1-954-555-0100",
        "email": "info@corexmed.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "12345 Executive Drive",
          "addressLocality": "Miramar",
          "addressRegion": "FL",
          "postalCode": "33025",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "25.9860",
          "longitude": "-80.3323"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": "09:00",
            "closes": "17:00"
          }
        ],
        "priceRange": "$$$",
        "medicalSpecialty": ["Internal Medicine","Geriatrics","Longevity Medicine","Regenerative Medicine"],
        "hasMap": "https://maps.google.com/?q=Miramar,FL",
        "sameAs": [
          "https://www.linkedin.com/company/corexmed",
          "https://www.instagram.com/corexmed"
        ]
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Physician",
        "name": "Dr. David Coradin",
        "description": "Double board-certified physician specializing in Internal Medicine, Geriatrics, Longevity Medicine, and Regenerative Medicine.",
        "medicalSpecialty": ["Internal Medicine","Geriatrics","Longevity Medicine","Regenerative Medicine"],
        "worksFor": {
          "@type": "MedicalBusiness",
          "name": "CorexMed",
          "url": import.meta.env.VITE_SITE_URL ?? 'https://example.com'
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Miramar",
          "addressRegion": "FL",
          "addressCountry": "US"
        }
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is executive medicine at CorexMed?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "CorexMed provides concierge executive medicine with 24/7 direct access to Dr. David Coradin, offering personalized longevity medicine, hormone optimization, AI cardiac testing, and comprehensive preventive care for high-level executives."
            }
          },
          {
            "@type": "Question",
            "name": "Does CorexMed offer telemedicine nationwide?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. CorexMed practices telemedicine across all 50 states, allowing patients to access Dr. Coradin from anywhere in the United States through secure virtual consultations."
            }
          },
          {
            "@type": "Question",
            "name": "What is AI-Powered Cardiac CT Angiography?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "CorexMed's AI-Powered Cardiac CT Angiography uses advanced artificial intelligence to analyze cardiac imaging with 98% accuracy, providing catheterization-level insights non-invasively in under 30 minutes."
            }
          },
          {
            "@type": "Question",
            "name": "Where is CorexMed located?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "CorexMed is located at 12345 Executive Drive, Miramar, FL 33025. We also offer nationwide telemedicine services."
            }
          }
        ]
      })}</script>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <h1 className={`text-2xl font-bold tracking-tight transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Playfair Display, serif' }}>
              CorexMed
            </h1>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#services" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>Services</a>
            <a href="#about" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>About Dr. Coradin</a>
            <a href="#testing" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>Testing</a>
            <a href="#location" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>Locations</a>
            <Link to="/education" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>Education</Link>
            <Link to="/signup" className={`text-sm font-medium transition-colors hover:text-amber-600 ${scrolled ? 'text-gray-700' : 'text-white/90'}`}>Sign Up</Link>
            <a href="#contact" className={`px-5 py-2 rounded-full border-2 text-sm font-medium whitespace-nowrap transition-all ${scrolled ? 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white' : 'border-white text-white hover:bg-white hover:text-gray-900'}`}>
              Patient Portal
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-6xl md:text-7xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Executive Medicine<br />
                <span className="italic">Redefined</span>
              </h2>
              <p className="text-xl text-white/70 mb-8 leading-relaxed max-w-xl">
                Comprehensive healthcare designed for leaders who demand excellence. Available wherever your life takes you.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-gray-900 rounded-full font-semibold text-sm hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
                  Schedule Consultation
                </Link>
                <a href="#portal" className="px-8 py-4 border-2 border-white text-white rounded-full font-semibold text-sm hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                  Patient Portal Login
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
            
            <div className="flex justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-600/20 rounded-full blur-3xl"></div>
                <div className="relative w-80 h-80 rounded-full overflow-hidden border-4 border-white/10 shadow-2xl">
                  <img 
                    src="https://static.readdy.ai/image/379863d3ba55e552d99479bd8d1c1df4/e61a754a9b0d430a67e603d2b4d82c61.png"
                    alt="Dr. David Coradin"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-amber-50 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-sm font-semibold text-amber-800">Double Board Certified</p>
              <p className="text-xs text-gray-600 mt-1">Internal Medicine & Geriatrics</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">Longevity Medicine</p>
              <p className="text-xs text-gray-600 mt-1">Certified Specialist</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">Regenerative Medicine</p>
              <p className="text-xs text-gray-600 mt-1">Advanced Therapies</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">Peptide Therapy</p>
              <p className="text-xs text-gray-600 mt-1">Hormone Optimization</p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold mb-6">
            THE COREXMED DIFFERENCE
          </div>
          <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            You Don't Become a Patient.<br />
            You Become Family.
          </h3>
          <p className="text-lg text-gray-600 leading-relaxed mb-12">
            At CorexMed, we redefine the doctor-patient relationship. As a member, you gain direct access to Dr. Coradin's personal line—available 24/7, wherever you are. We function as the quarterback for your health, coordinating every aspect of your care with precision and dedication. Whether managing chronic illness or optimizing for longevity, we're with you every step of the way.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-phone-line text-2xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">24/7 Direct Access</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Reach Dr. Coradin anytime, anywhere. Your health doesn't wait for office hours, and neither do we.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-global-line text-2xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Telemedicine Nationwide</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Practice medicine across all 50 states. Your healthcare travels with you, seamlessly.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-user-heart-line text-2xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Personalized Care Plans</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Every protocol is tailored to your unique biology, lifestyle, and health goals. No cookie-cutter approaches.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-test-tube-line text-2xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Testing</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Advanced diagnostics that go beyond standard care, revealing the complete picture of your health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-24 bg-gradient-to-br from-gray-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Comprehensive Care Tailored to You
            </h3>
            <p className="text-lg text-white/70">
              Blending functional, integrative, and traditional medicine with modern science
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:border-amber-600/50 transition-all hover:-translate-y-1">
              <div className="h-1 w-16 bg-amber-600 mb-6"></div>
              <h4 className="text-2xl font-bold text-white mb-6">Preventive & Longevity</h4>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>DNA Testing & Genetic Analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Longevity Panels & Biomarkers</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Hormone Optimization Therapy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Bioidentical Hormone Replacement</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Peptide Therapy Programs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Age Management Protocols</span>
                </li>
              </ul>
              <a href="#learn-more" className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm mt-6 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:border-amber-600/50 transition-all hover:-translate-y-1">
              <div className="h-1 w-16 bg-amber-600 mb-6"></div>
              <h4 className="text-2xl font-bold text-white mb-6">Integrative Medicine</h4>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Functional Medicine Approach</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Comprehensive Vitamin Panels</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Mineral Analysis & Optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Advanced Stool Studies</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Gut Health & Microbiome Testing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Nutritional Optimization Plans</span>
                </li>
              </ul>
              <a href="#learn-more" className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm mt-6 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:border-amber-600/50 transition-all hover:-translate-y-1">
              <div className="h-1 w-16 bg-amber-600 mb-6"></div>
              <h4 className="text-2xl font-bold text-white mb-6">Chronic Care Management</h4>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Cardiovascular Health Panels</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Comprehensive Allergy Testing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Regenerative Therapy Options</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Chronic Disease Coordination</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Metabolic Health Optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-amber-600 mt-1"></i>
                  <span>Quarterback Health Management</span>
                </li>
              </ul>
              <a href="#learn-more" className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm mt-6 hover:gap-3 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AI Cardiac Testing Section - NEW */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-amber-600/20 border border-amber-600/30 text-amber-400 rounded-full text-xs font-semibold mb-6">
                CUTTING-EDGE TECHNOLOGY
              </div>
              <h3 className="text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                AI-Powered Cardiac<br />
                <span className="text-amber-500">CT Angiography</span>
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Experience the future of cardiac diagnostics with our state-of-the-art AI-enhanced CT scan technology. This revolutionary non-invasive procedure provides the precision of traditional cardiac catheterization without the risks, recovery time, or discomfort.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-brain-line text-2xl text-amber-500"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">AI-Enhanced Analysis</h4>
                    <p className="text-sm text-gray-400">Advanced artificial intelligence algorithms analyze your cardiac imaging with unprecedented accuracy, detecting issues traditional methods might miss.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-amber-500"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Non-Invasive Precision</h4>
                    <p className="text-sm text-gray-400">Get catheterization-level insights without invasive procedures. No hospital stay, no arterial puncture, no extended recovery period.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-amber-500"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Rapid Results</h4>
                    <p className="text-sm text-gray-400">Complete cardiac assessment in minutes, not hours. Walk in, get scanned, and return to your day with comprehensive cardiac insights.</p>
                  </div>
                </div>
              </div>
              
              <a href="#cardiac-testing" className="inline-flex items-center gap-3 px-8 py-4 bg-amber-600 text-white rounded-full font-semibold text-sm hover:bg-amber-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
                Learn More About AI Cardiac Testing
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-amber-600/10 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <div className="w-full h-96">
                  <img 
                    src="https://readdy.ai/api/search-image?query=advanced%20medical%20CT%20scanner%20machine%20in%20modern%20hospital%20room%20with%20glowing%20blue%20screens%20displaying%20heart%20imaging%20artificial%20intelligence%20technology%20clean%20professional%20medical%20facility%20futuristic%20healthcare%20equipment%20simple%20background&width=600&height=600&seq=cardiac-ct-ai&orientation=squarish"
                    alt="AI-Powered Cardiac CT Scanner"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 text-center">
                  <p className="text-3xl font-bold text-amber-500 mb-1">98%</p>
                  <p className="text-xs text-gray-400">Accuracy Rate</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 text-center">
                  <p className="text-3xl font-bold text-amber-500 mb-1">&lt;30</p>
                  <p className="text-xs text-gray-400">Minutes</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10 text-center">
                  <p className="text-3xl font-bold text-amber-500 mb-1">0</p>
                  <p className="text-xs text-gray-400">Invasive Risk</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialist Consultations Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold mb-6">
              ENHANCED CARE OPTIONS
            </div>
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Specialist Consultations On Demand
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Access top-tier specialists as needed for consultations, follow-ups, and second opinions. These services complement your CorexMed membership, ensuring comprehensive care across all medical disciplines.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-heart-pulse-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Cardiology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Expert cardiovascular care for heart health optimization, risk assessment, and advanced cardiac diagnostics.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-lungs-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Pulmonology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Respiratory health specialists for breathing optimization, lung function testing, and pulmonary wellness.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-capsule-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Gastroenterology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Digestive health experts for gut optimization, advanced GI diagnostics, and comprehensive digestive care.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-hand-heart-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Rheumatology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Joint and autoimmune specialists for inflammatory conditions, arthritis management, and mobility optimization.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-restaurant-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Nutritionist</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Personalized nutrition planning for optimal performance, metabolic health, and longevity-focused dietary strategies.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-microscope-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Oncology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Cancer care specialists for prevention strategies, second opinions, and comprehensive oncology consultations.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-drop-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Hematology</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Blood health specialists for comprehensive blood disorder management and hematologic optimization.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-user-heart-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Geriatrics</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Age-focused care for healthy aging, cognitive health, and comprehensive geriatric assessments.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-stethoscope-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Orthopedics</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Musculoskeletal specialists for joint health, injury prevention, and comprehensive orthopedic care.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-run-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Fitness Coach</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Expert strength training and conditioning programs designed for executive performance and longevity optimization.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-mental-health-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Psychiatry & Mental Health</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Comprehensive mental health support for stress management, performance optimization, and emotional wellness.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-all">
                <i className="ri-team-line text-2xl text-amber-700 group-hover:text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Social Worker</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Holistic support for life transitions, care coordination, and navigating complex healthcare and social systems.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600 mb-6">
              All specialist consultations are coordinated by Dr. Coradin to ensure seamless, integrated care
            </p>
            <a href="#specialists" className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full font-semibold text-sm hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
              Learn More About Specialist Services
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center gap-1 mb-8">
            {[1, 2, 3, 4, 5].map((star) => (
              <i key={star} className="ri-star-fill text-amber-600 text-xl"></i>
            ))}
          </div>
          
          <div className="relative">
            <div className="text-center">
              <p className="text-2xl text-gray-800 leading-relaxed mb-8 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                "{testimonials[currentTestimonial].quote}"
              </p>
              
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-20 h-20 rounded-full overflow-hidden">
                  <img 
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-sm text-gray-600">{testimonials[currentTestimonial].title}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-center gap-3">
              <button 
                onClick={prevTestimonial}
                className="w-12 h-12 border-2 border-amber-600 bg-gray-900 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all flex items-center justify-center cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button 
                onClick={nextTestimonial}
                className="w-12 h-12 border-2 border-amber-600 bg-gray-900 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all flex items-center justify-center cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Testing */}
      <section id="testing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <h3 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Precision Diagnostics That Go Beyond Standard Care
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We don't practice with the standard of care—we create the standard of care. Our comprehensive testing panels provide unprecedented insight into your health, enabling truly personalized and precise medicine.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  'DNA Analysis',
                  'Hormone Panels',
                  'Cardiovascular',
                  'Longevity Markers',
                  'Vitamin Levels',
                  'Mineral Analysis',
                  'Allergy Testing',
                  'Gut Health'
                ].map((test) => (
                  <div 
                    key={test}
                    className="px-4 py-3 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:border-amber-600 hover:text-amber-700 transition-all cursor-pointer text-center"
                  >
                    {test}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="md:col-span-2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <div className="w-full h-96">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20medical%20laboratory%20equipment%20advanced%20diagnostic%20testing%20machines%20clean%20professional%20medical%20facility%20bright%20lighting%20high%20tech%20healthcare%20technology%20simple%20background&width=500&height=600&seq=lab1&orientation=portrait"
                    alt="Advanced Medical Testing"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 italic mt-3 text-center">
                State-of-the-art diagnostics personalized to your health goals
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Access */}
      <section id="location" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Healthcare On Your Terms, Wherever You Are
            </h3>
            <p className="text-lg text-gray-600">
              Three ways to access world-class medical care
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                <i className="ri-computer-line text-3xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Nationwide Virtual Care</h4>
              <p className="text-sm text-gray-600 text-center leading-relaxed mb-4">
                Access Dr. Coradin from anywhere in the United States through secure telemedicine consultations. Your healthcare travels with you.
              </p>
              <div className="text-center">
                <a href="#telemedicine" className="text-amber-600 font-semibold text-sm hover:text-amber-700 cursor-pointer">
                  Schedule Virtual Visit →
                </a>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                <i className="ri-map-pin-line text-3xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">Miramar, Florida</h4>
              <p className="text-sm text-gray-600 text-center leading-relaxed mb-4">
                Visit our state-of-the-art office for comprehensive in-person consultations and advanced diagnostic testing.
              </p>
              <div className="text-center text-sm text-gray-700 mb-4">
                <p className="font-semibold">12345 Executive Drive</p>
                <p>Miramar, FL 33025</p>
                <p className="mt-2">Mon-Fri: 9AM - 5PM</p>
              </div>
              <div className="text-center">
                <a href="#directions" className="text-amber-600 font-semibold text-sm hover:text-amber-700 cursor-pointer">
                  Get Directions →
                </a>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                <i className="ri-phone-line text-3xl text-amber-700"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">24/7 Direct Line</h4>
              <p className="text-sm text-gray-600 text-center leading-relaxed mb-4">
                As a CorexMed member, you have Dr. Coradin's direct number. Reach him anytime, anywhere—because your health doesn't wait.
              </p>
              <div className="text-center">
                <a href="#membership" className="text-amber-600 font-semibold text-sm hover:text-amber-700 cursor-pointer">
                  Learn About Membership →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="consultation" className="py-24 bg-gradient-to-br from-emerald-900 to-teal-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Experience Executive Medicine?
          </h3>
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            Join the CorexMed family and gain a trusted partner in your health journey
          </p>
          <Link to="/signup" className="inline-flex items-center gap-3 px-10 py-5 bg-white text-gray-900 rounded-full font-bold text-base hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap cursor-pointer">
            Schedule Your Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
          <p className="text-sm text-white/60 mt-6">
            Initial consultation includes comprehensive health assessment
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white pt-16 pb-8 rounded-t-3xl mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>CorexMed</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                Executive Medicine for Exceptional Lives
              </p>
              <div className="absolute text-9xl font-bold text-gray-800/20 -mt-8 pointer-events-none" style={{ fontFamily: 'Playfair Display, serif' }}>
                CorexMed
              </div>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Navigate</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#about" className="hover:text-amber-600 transition-colors cursor-pointer">About Dr. Coradin</a></li>
                <li><a href="#services" className="hover:text-amber-600 transition-colors cursor-pointer">Services</a></li>
                <li><a href="#testing" className="hover:text-amber-600 transition-colors cursor-pointer">Testing & Diagnostics</a></li>
                <li><Link to="/education" className="hover:text-amber-600 transition-colors">Education</Link></li>
                <li><a href="#resources" className="hover:text-amber-600 transition-colors cursor-pointer">Patient Resources</a></li>
                <li><a href="#blog" className="hover:text-amber-600 transition-colors cursor-pointer">Blog & Articles</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">For Patients</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="#portal" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-lock-line"></i> Portal Login
                  </a>
                </li>
                <li>
                  <a href="#schedule" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-calendar-line"></i> Schedule Appointment
                  </a>
                </li>
                <li>
                  <a href="#telemedicine" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-video-line"></i> Telemedicine Access
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-amber-600 transition-colors flex items-center gap-2 cursor-pointer">
                    <i className="ri-message-line"></i> Contact Us
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Health Insights</h5>
              <p className="text-sm text-gray-400 mb-4">
                Receive executive health tips and longevity research
              </p>
              <form className="flex gap-2" data-readdy-form id="newsletter-form" onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const data = new URLSearchParams();
                formData.forEach((value, key) => {
                  data.append(key, value.toString());
                });
                fetch('https://readdy.ai/api/form/d4mifgrifile0aqbfo40', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: data.toString()
                }).then(() => {
                  alert('Thank you for subscribing!');
                  e.currentTarget.reset();
                }).catch(() => {
                  alert('Subscription failed. Please try again.');
                });
              }}>
                <input 
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600"
                />
                <button 
                  type="submit"
                  className="w-10 h-10 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
              <div className="flex gap-3 mt-6">
                <a href="#linkedin" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#instagram" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>&copy; 2025 CorexMed. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#privacy" className="hover:text-amber-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#terms" className="hover:text-amber-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
